import * as React from 'react'
import { Utils, TLBounds } from '@tldraw/core'
import { Vec } from '@tldraw/vec'
import {TransformInfo, createDefaultStyle, TDBaseShape} from '@modules/whiteboard'
import {
  intersectBoundsBounds,
  intersectBoundsPolyline,
  intersectLineSegmentBounds,
  intersectLineSegmentLineSegment,
} from '@tldraw/intersect'
import {TDShapeUtil} from '@modules/whiteboard/shapes/TDShapeUtil'
import {DrawComponent, DrawComponentStore} from './DrawComponent'
import { DrawIndicator } from './DrawIndicator'

export interface DrawShape extends TDBaseShape {
  type: 'draw'
  points: number[][]
  isComplete: boolean
}

export type ShapeWithPoints = { point: number[], points: number[][], id: string }

type T = DrawShape
type E = SVGSVGElement

export class DrawUtil extends TDShapeUtil<T, E> {
  type = 'draw' as const

  getShape = (props: Partial<T>): T => {
    return Utils.deepMerge<T>(
      {
        id: 'id',
        type: 'draw',
        name: 'Draw',
        parentId: 'page',

        childIndex: 1,
        point: [0, 0],
        rotation: 0,
        style: createDefaultStyle(),
        points: [],
        isComplete: false,
      },
      props
    )
  }

  Component = DrawComponent as any

  // TDShapeUtil.Component<T, E, TDMeta>((props, ref) => {
  //   return <DrawComponent {...props} ref={ref} />
  // })

  Indicator = DrawIndicator

  transform = (
    shape: T,
    bounds: TLBounds,
    { initialShape, scaleX, scaleY }: TransformInfo<T>
  ): Partial<T> => {
    const initialShapeBounds = Utils.getBoundsFromPoints(initialShape.points)

    const points = initialShape.points.map(([x, y, r]) => {
      return [
        bounds.width *
          (scaleX < 0 // * sin?
            ? 1 - x / initialShapeBounds.width
            : x / initialShapeBounds.width),
        bounds.height *
          (scaleY < 0 // * cos?
            ? 1 - y / initialShapeBounds.height
            : y / initialShapeBounds.height),
        r,
      ]
    })

    const newBounds = Utils.getBoundsFromPoints(shape.points)

    const point = Vec.sub([bounds.minX, bounds.minY], [newBounds.minX, newBounds.minY])

    return {
      points,
      point,
    }
  }

  componentStore: {[id: string]: DrawComponentStore} = {}

  getBounds = (shape: ShapeWithPoints) => {
    const {componentStore} = this
    if (componentStore[shape.id]?.bounds) {
      return componentStore[shape.id].bounds
    }
    else {
      const bounds = Utils.getBoundsFromPoints(shape.points)
      return Utils.translateBounds(bounds, shape.point)
    }

    // // The goal here is to avoid recalculating the bounds from the
    // // points array, which is expensive. However, we still need a
    // // new bounds if the point has changed, but we will reuse the
    // // previous bounds-from-points result if we can.
    //
    // const pointsHaveChanged = !this.pointsBoundsCache.has(shape.points)
    // const pointHasChanged = !(this.pointCache[shape.id] === shape.point)
    //
    // if (pointsHaveChanged) {
    //   // If the points have changed, then bust the points cache
    //   const bounds = Utils.getBoundsFromPoints(shape.points)
    //   this.pointsBoundsCache.set(shape.points, bounds)
    //   this.shapeBoundsCache.set(shape.id, Utils.translateBounds(bounds, shape.point))
    //   this.pointCache[shape.id] = shape.point
    // } else if (pointHasChanged && !pointsHaveChanged) {
    //   // If the point have has changed, then bust the point cache
    //   this.pointCache[shape.id] = shape.point
    //   this.shapeBoundsCache.set(
    //     shape.id,
    //     // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    //     Utils.translateBounds(this.pointsBoundsCache.get(shape.points)!, shape.point)
    //   )
    // }
    //
    // // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    // return this.shapeBoundsCache.get(shape.id)!
  }

  shouldRender = (prev: T, next: T) => {
    return true
  }

  hitTestPoint = (shape: ShapeWithPoints, point: number[]) => {
    const ptA = Vec.sub(point, shape.point)
    return Utils.pointInPolyline(ptA, shape.points)
  }

  hitTestLineSegment = (shape: ShapeWithPoints, A: number[], B: number[]): boolean => {
    const { points, point } = shape
    const ptA = Vec.sub(A, point)
    const ptB = Vec.sub(B, point)
    const bounds = this.getBounds(shape)

    if (points.length <= 2) {
      return Vec.distanceToLineSegment(A, B, shape.point) < 4
    }

    if (intersectLineSegmentBounds(ptA, ptB, bounds)) {
      for (let i = 1; i < points.length; i++) {
        if (intersectLineSegmentLineSegment(points[i - 1], points[i], ptA, ptB).didIntersect) {
          return true
        }
      }
    }

    return false
  }

  hitTestBounds = (shape: T, bounds: TLBounds) => {
    // Test axis-aligned shape
    if (!shape.rotation) {
      const shapeBounds = this.getBounds(shape)

      return (
        Utils.boundsContain(bounds, shapeBounds) ||
        ((Utils.boundsContain(shapeBounds, bounds) ||
          intersectBoundsBounds(shapeBounds, bounds).length > 0) &&
          intersectBoundsPolyline(Utils.translateBounds(bounds, Vec.neg(shape.point)), shape.points)
            .length > 0)
      )
    }

    // Test rotated shape
    const rBounds = this.getRotatedBounds(shape)

    const c = Utils.getBoundsCenter(Utils.getBoundsFromPoints(shape.points))
    const rotatedBounds = shape.points.map((pt) => Vec.rotWith(pt, c, shape.rotation || 0))

    return (
      Utils.boundsContain(bounds, rBounds) ||
      intersectBoundsPolyline(Utils.translateBounds(bounds, Vec.neg(shape.point)), rotatedBounds)
        .length > 0
    )
  }
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/draw/DrawUtil.tsx